/**
 * Format a pay-range amount with K/M/B suffixes.
 * Exact multiples drop the trailing .0 (140K, not 140.0K).
 * Examples: 14.4, 125.4K, 140K, 1.2M, 10B
 */
export function formatPayRangeAmount(value) {
  if (value == null || value === "" || value === "-") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);

  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);

  if (abs >= 1_000_000_000) {
    return `${sign}${formatScaled(abs / 1_000_000_000)}B`;
  }
  if (abs >= 1_000_000) {
    return `${sign}${formatScaled(abs / 1_000_000)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}${formatScaled(abs / 1_000)}K`;
  }
  return `${sign}${formatScaled(abs)}`;
}

function formatScaled(n) {
  return n.toFixed(1).replace(/\.0$/, "");
}

/** "$" for USD, "CA$" for CAD, "€" for EUR; falls back to the code itself when no symbol exists. */
export function getCurrencySymbol(currencyCode) {
  const code = String(currencyCode || "").toUpperCase();
  if (!code) return "";
  try {
    const part = new Intl.NumberFormat("en-US", { style: "currency", currency: code })
      .formatToParts(0)
      .find((p) => p.type === "currency");
    return part?.value || code;
  } catch {
    return code;
  }
}

/** Range shown on the draft's period bar, e.g. "$52K – $84K" or "CA$49.8K – CA$84.8K". */
export function formatPayRangeBarLabel(minAmount, maxAmount, currencyCode) {
  const has = (v) => v != null && v !== "";
  if (!has(minAmount) && !has(maxAmount)) return String(currencyCode || "");
  const symbol = getCurrencySymbol(currencyCode);
  const part = (v) => (has(v) ? `${symbol}${formatPayRangeAmount(v)}` : "—");
  return `${part(minAmount)} – ${part(maxAmount)}`;
}

/** Amount input text with thousands separators and the currency's decimals, e.g. "84,822.40". */
export function formatPayRangeInputAmount(value, decimalPlaces = 2) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
}

/** Whole-unit currency text with a plain space after the code, e.g. "RON 6,861" or "$6,861". */
export function formatPayRangeWholeAmount(value, currencyCode) {
  if (value == null || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(n)
      .replace(/\u00a0/g, " ");
  } catch {
    return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
  }
}
